import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, Chip, IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { v4 as uuidv4 } from 'uuid';

const PRESET_TABS = ['General', 'Sensor Data', 'Live Data', 'Simulation', 'Status', 'Trend'];

function DTDashboardCreateDialog({ open, componentId, onConfirm, onClose, theme }) {
  const isDark = theme === 'dark';

  const [name, setName] = useState('');
  const [tabs, setTabs] = useState([{ id: uuidv4(), label: 'Overview' }]);
  const [customTabInput, setCustomTabInput] = useState('');

  useEffect(() => {
    if (open) {
      setName(componentId ? `${componentId} Dashboard` : 'New Dashboard');
      setTabs([{ id: uuidv4(), label: 'Overview' }]);
      setCustomTabInput('');
    }
  }, [open, componentId]);

  const addTab = (label) => {
    const l = label.trim();
    if (!l || tabs.some((t) => t.label.toLowerCase() === l.toLowerCase())) return;
    setTabs((prev) => [...prev, { id: uuidv4(), label: l }]);
    setCustomTabInput('');
  };

  const removeTab = (id) => {
    if (tabs.length <= 1) return;
    setTabs((prev) => prev.filter((t) => t.id !== id));
  };

  const handleConfirm = () => {
    if (!name.trim() || tabs.length === 0) return;
    onConfirm(
      name.trim(),
      tabs.map((t) => ({ ...t, widgets: [] }))
    );
  };

  const bgMain = isDark ? '#1a1a1a' : '#ffffff';
  const bgInput = isDark ? '#252525' : '#ffffff';
  const borderColor = isDark ? '#333' : '#e0e0e0';
  const textColor = isDark ? '#ffffff' : '#000000';
  const mutedColor = isDark ? '#888' : '#777';

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: bgInput,
      color: textColor,
      '& fieldset': { borderColor },
      '&:hover fieldset': { borderColor: isDark ? '#555' : '#bbb' },
    },
    '& .MuiInputLabel-root': { color: mutedColor },
    '& .MuiInputLabel-root.Mui-focused': { color: isDark ? '#90caf9' : undefined },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: bgMain, color: textColor } }}
    >
      <DialogTitle sx={{ borderBottom: `1px solid ${borderColor}`, fontWeight: 700, fontSize: 16 }}>
        Create Dashboard
        {componentId && (
          <Typography variant="caption" sx={{ display: 'block', color: mutedColor, fontWeight: 400, mt: 0.25 }}>
            Component: {componentId}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5, pb: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Dashboard heading */}
        <TextField
          label="Dashboard Heading"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          size="small"
          sx={inputSx}
        />

        {/* Tab configuration */}
        <Box>
          <Typography variant="subtitle2" sx={{ color: textColor, fontWeight: 700, mb: 1 }}>
            Tabs
          </Typography>
          <Typography variant="caption" sx={{ color: mutedColor, display: 'block', mb: 1 }}>
            Configure the tabs that will appear in this dashboard. At least one tab is required.
          </Typography>

          {/* Current tabs */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5, minHeight: 32 }}>
            {tabs.map((tab) => (
              <Chip
                key={tab.id}
                label={tab.label}
                onDelete={tabs.length > 1 ? () => removeTab(tab.id) : undefined}
                sx={{
                  backgroundColor: isDark ? '#1e3a5f' : '#e3f2fd',
                  color: isDark ? '#90caf9' : '#1565c0',
                  fontWeight: 600,
                  border: `1px solid ${isDark ? '#1565c0' : '#90caf9'}`,
                  '& .MuiChip-deleteIcon': { color: isDark ? '#64b5f6' : '#1565c0' },
                }}
              />
            ))}
          </Box>

          {/* Quick-add presets */}
          <Typography variant="caption" sx={{ color: mutedColor, display: 'block', mb: 0.75 }}>
            Quick add:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
            {PRESET_TABS.filter((p) => !tabs.some((t) => t.label === p)).map((preset) => (
              <Chip
                key={preset}
                label={`+ ${preset}`}
                size="small"
                onClick={() => addTab(preset)}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: isDark ? '#252525' : '#f5f5f5',
                  color: isDark ? '#ccc' : '#555',
                  border: `1px solid ${borderColor}`,
                  '&:hover': { backgroundColor: isDark ? '#303030' : '#e8e8e8' },
                }}
              />
            ))}
          </Box>

          {/* Custom tab name input */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              placeholder="Custom tab name..."
              value={customTabInput}
              onChange={(e) => setCustomTabInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addTab(customTabInput); }}
              size="small"
              sx={{ flex: 1, ...inputSx }}
            />
            <IconButton
              onClick={() => addTab(customTabInput)}
              size="small"
              sx={{
                border: `1px solid ${borderColor}`,
                borderRadius: 1,
                color: textColor,
                '&:hover': { backgroundColor: isDark ? '#252525' : '#f5f5f5' },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${borderColor}`, px: 2, py: 1.5, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{ textTransform: 'none', color: mutedColor }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!name.trim() || tabs.length === 0}
          sx={{ textTransform: 'none' }}
        >
          Create Dashboard
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DTDashboardCreateDialog;
